import type {
  DroppedDraftSummary,
  TwinLaunchBiOutcome,
  TwinLaunchHistoryResponse,
  TwinLaunchSummary,
} from '@repo/frontend-types';
import type { TagTone } from '@repo/design-system';

import { launchVersion } from './versionsText';

/**
 * The rules of Versions' launch table (`twin-details-page-versions`, R5; first written for the Twin
 * Details history panel it replaced, `twin-launch-history-panel`) — pure, so every decision the table
 * makes is testable without rendering it. `VersionsModal` only draws rows.
 */

/** The footnote under the table (R3.3): the history starts with the launch record. */
export const HISTORY_FOOTNOTE =
  "Launches are recorded from 5 October 2026. Earlier configuration versions still count towards the 16 kept, but can't be loaded here.";

export interface HistoryTag {
  tone: TagTone;
  label: string;
}

const BI_TAG: Record<TwinLaunchBiOutcome['outcome'], HistoryTag> = {
  updated: { tone: 'success', label: 'BI updated' },
  updated_with_gaps: { tone: 'warn', label: 'BI updated with gaps' },
  failed: { tone: 'error', label: 'BI failed' },
  not_updated: { tone: 'warn', label: 'BI not updated' },
};

/** Never "failed" for a launch with no outcome: it was not recorded (R3.4, epic ledger row 33). */
const NOT_RECORDED: HistoryTag = { tone: 'neutral', label: 'BI not recorded' };

export const biOutcomeTag = (outcome: TwinLaunchBiOutcome | null): HistoryTag =>
  outcome ? BI_TAG[outcome.outcome] : NOT_RECORDED;

export type HistoryRow =
  | { kind: 'dropped'; key: 'dropped'; dropped: DroppedDraftSummary }
  | { kind: 'dropped-unread'; key: 'dropped-unread' }
  | {
      kind: 'launch';
      key: string;
      launch: TwinLaunchSummary;
      status: 'completed' | 'in-progress' | 'stopped';
      production: boolean;
      /** *v{N}* by the launch number (`twin-details-page-launch-numbers`), or null when it has none. */
      version: string | null;
      bi: HistoryTag | null;
      /** *Load as Draft* is offered: a completed launch that is not production. */
      canLoad: boolean;
    };

const statusOf = (launch: TwinLaunchSummary, serverNowMs: number, timeoutMs: number) => {
  if (launch.completedAt) return 'completed' as const;
  const started = Date.parse(launch.launchedAt);
  // An unreadable start is not called "in progress" for ever: it is stopped.
  return Number.isFinite(started) && serverNowMs - started < timeoutMs
    ? ('in-progress' as const)
    : ('stopped' as const);
};

/**
 * Versions' table rows, top to bottom (`twin-details-page-versions`, R5): every listed launch newest
 * first, then the dropped draft (or the note that it could not be checked). Load is offered on a
 * COMPLETED launch that is not production — never on production, where *Discard Draft* is the way
 * back (ruling 3), and never on a launch in progress or stopped (its plan was never production, and
 * the plan route refuses it).
 */
export const historyRows = (args: {
  history: TwinLaunchHistoryResponse;
  dropped: DroppedDraftSummary | null;
  droppedReadFailed: boolean;
}): HistoryRow[] => {
  const { history, dropped, droppedReadFailed } = args;
  const rows: HistoryRow[] = [];
  const serverNowMs = Date.parse(history.serverNow);
  for (const launch of history.launches) {
    const status = statusOf(launch, serverNowMs, history.claimTimeoutSeconds * 1000);
    const production = launch.launchUuid === history.productionLaunchUuid;
    const completed = status === 'completed';
    rows.push({
      kind: 'launch',
      key: launch.launchUuid,
      launch,
      status,
      production,
      version: launchVersion(launch),
      bi: completed ? biOutcomeTag(launch.biOutcome) : null,
      canLoad: completed && !production,
    });
  }
  rows.push(...droppedRows({ dropped, droppedReadFailed }));
  return rows;
};

/**
 * The dropped-draft row alone (or the note that it could not be checked). It depends only on the
 * dropped-draft read, so it is shown even when the history read failed — Restore lives on it.
 */
export const droppedRows = (args: {
  dropped: DroppedDraftSummary | null;
  droppedReadFailed: boolean;
}): HistoryRow[] =>
  args.dropped
    ? [{ kind: 'dropped', key: 'dropped', dropped: args.dropped }]
    : args.droppedReadFailed
      ? [{ kind: 'dropped-unread', key: 'dropped-unread' }]
      : [];

/**
 * What a launch's plan held, in a few words (R5): its floors, from the summary stored at launch
 * (`twin-details-page-launch-numbers`). `null` when the launch has no summary (an older middleware
 * sends none, so `planSummary` is read with `== null`).
 */
export const planSummaryText = (launch: TwinLaunchSummary): string | null => {
  if (launch.planSummary == null) return null;
  const n = launch.planSummary.floors;
  return `${n} ${n === 1 ? 'floor' : 'floors'}`;
};
